import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthContext } from '../contexts/AuthContext';
import type { Database } from '../types/supabase';
import { useCachedData, globalCache } from './useCachedData';

type Profile = Database['public']['Tables']['profiles']['Row'];

export function useProfile() {
  const { user } = useAuthContext();
  const isMountedRef = useRef(true);

  // Utiliser useCachedData pour le profil
  const { data: profile, invalidateCache, refresh } = useCachedData<Profile>(
    `profile-${user?.id}`,
    async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      if (error) {
        if (error.code === 'PGRST116') {
          await createProfile();
          return null;
        }
        throw error;
      }
      return data;
    },
    30 * 60 * 1000, // 30 minutes
    [user?.id]
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const createProfile = async () => {
    try {
      if (!user || !user.email) {
        console.error("Cannot create profile: user or email is missing");
        return;
      }
      
      const newProfile = {
        id: user.id,
        email: user.email,
        full_name: user.user_metadata?.full_name || user.user_metadata?.name || null,
        avatar_url: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('profiles')
        .insert([newProfile])
        .select()
        .single();
        
      if (error) {
        console.error("Error creating profile:", error.message);
        throw error;
      }
      
      await invalidateCache();
      return data;
    } catch (error) {
      console.error("Failed to create profile:", error);
      return null;
    }
  };

  const updateProfile = async (updates: Partial<Profile>, shouldInvalidateCache = true) => {
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user?.id)
        .select()
        .single();

      if (error) {
        console.error("Error updating profile:", error.message);
        throw error;
      }
      
      // Invalider tous les caches liés aux profils et aux recherches
      if (shouldInvalidateCache) {
        // Invalider le cache du profil
        await invalidateCache();
        
        // Invalider tous les caches de recherche dans sessionStorage
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('search_')) {
            sessionStorage.removeItem(key);
          }
        });
        
        // Invalider les caches globaux liés aux profils et aux recherches
        Object.keys(globalCache).forEach(key => {
          if (key.includes('profile') || 
              key.includes('search') || 
              key.includes('relations') ||
              key.includes('creator') || 
              key.includes('certifier')) {
            delete globalCache[key];
          }
        });
      }
      
      return { error: null };
    } catch (error) {
      console.error('Error updating profile:', error);
      return { error };
    }
  };

  const uploadAvatar = async (file: File) => {
    try {
      const filePath = `${user?.id}/avatar.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { 
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (uploadError) {
        console.error("Error uploading avatar:", uploadError.message);
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const timestamp = Date.now();
      const urlWithTimestamp = `${publicUrl}?v=${timestamp}`;

      const { error: updateError } = await updateProfile({
        avatar_url: urlWithTimestamp,
      });

      if (updateError) throw updateError;

      // Mettre à jour toutes les relations où l'utilisateur est créateur
      try {
        const { error: relationsUpdateError } = await supabase
          .from('relations')
          .update({ 
            creator_avatar: urlWithTimestamp,
            updated_at: new Date().toISOString()
          })
          .eq('creator_id', user?.id);

        if (relationsUpdateError) {
          console.error("Erreur lors de la mise à jour des avatars dans les relations:", relationsUpdateError);
        } else {
          console.log("Avatars mis à jour dans toutes les relations créées par l'utilisateur");
          
          // Invalider le cache des relations
          // Parcourir le globalCache et supprimer toutes les entrées liées aux relations
          for (const cacheKey of Object.keys(globalCache)) {
            if (cacheKey.includes('relations') || 
                cacheKey.includes('creator') || 
                cacheKey.includes('certifier') ||
                cacheKey.includes('user-relations')) {
              console.log(`Invalidation du cache de relation: ${cacheKey}`);
              delete globalCache[cacheKey];
            }
          }
        }
      } catch (relationsError) {
        console.error("Exception lors de la mise à jour des avatars dans les relations:", relationsError);
      }

      const img = new Image();
      img.src = urlWithTimestamp;

      // Invalider le cache après la mise à jour de l'avatar
      await invalidateCache();

      return { publicUrl: urlWithTimestamp, error: null };
    } catch (error) {
      console.error('Error uploading avatar:', error);
      return { publicUrl: null, error };
    }
  };

  // Fonction pour invalider uniquement les caches liés aux photos de profil
  // sans affecter les autres paramètres du formulaire Settings
  const safeInvalidatePhotoCache = async () => {
    // Obtenir l'ID du profil
    if (!user?.id) return;
    
    // Identifier les clés de cache spécifiques aux photos
    const photoCacheKeys = Object.keys(globalCache).filter(cacheKey => 
      (cacheKey.includes('photo') || cacheKey.includes('avatar')) && 
      cacheKey.includes(user.id)
    );
    
    console.log('Invalidation ciblée des caches de photos:', photoCacheKeys);
    
    // Supprimer seulement ces caches spécifiques
    photoCacheKeys.forEach(cacheKey => {
      delete globalCache[cacheKey];
    });
    
    // Mettre à jour le cache du profil sans l'invalider complètement
    // Récupérer les nouvelles données de photo uniquement
    try {
      const { data } = await supabase
        .from('profiles')
        .select('avatar_url, photos')
        .eq('id', user.id)
        .single();
      
      if (data && globalCache[`profile-${user?.id}`]) {
        // Mettre à jour uniquement les champs avatar_url et photos dans le cache existant
        const cachedProfile = globalCache[`profile-${user?.id}`].data;
        if (cachedProfile) {
          cachedProfile.avatar_url = data.avatar_url;
          cachedProfile.photos = data.photos;
          
          // Rafraîchir le timestamp du cache
          globalCache[`profile-${user?.id}`].timestamp = Date.now();
          globalCache[`profile-${user?.id}`].expiresAt = Date.now() + (30 * 60 * 1000);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour ciblée du cache de photos:', error);
      // En cas d'erreur, on invalide quand même le cache complet pour garantir la cohérence
      await invalidateCache();
    }
  };

  const uploadProfilePhoto = async (file: File, index: number | null = null) => {
    try {
      // Générer un nom de fichier unique
      const timestamp = Date.now();
      const randomStr = Math.random().toString(36).substring(2, 10);
      const fileName = `${timestamp}-${randomStr}.jpg`;
      const filePath = `${user?.id}/${fileName}`;

      // Télécharger le fichier dans le bucket "profilephotos"
      const { error: uploadError } = await supabase.storage
        .from('profilephotos')
        .upload(filePath, file, { 
          upsert: true,
          contentType: 'image/jpeg'
        });

      if (uploadError) {
        console.error("Erreur lors du téléchargement de la photo:", uploadError.message);
        throw uploadError;
      }

      // Obtenir l'URL publique du fichier téléchargé
      const { data: { publicUrl } } = supabase.storage
        .from('profilephotos')
        .getPublicUrl(filePath);

      const urlWithTimestamp = `${publicUrl}?v=${timestamp}`;

      // Si l'index est null, c'est la photo de profil principale
      if (index === null) {
        const { error: updateError } = await updateProfile({
          avatar_url: urlWithTimestamp,
        });

        if (updateError) throw updateError;

        // Mettre à jour les avatars dans les relations
        try {
          const { error: relationsUpdateError } = await supabase
            .from('relations')
            .update({ 
              creator_avatar: urlWithTimestamp,
              updated_at: new Date().toISOString()
            })
            .eq('creator_id', user?.id);

          if (relationsUpdateError) {
            console.error("Erreur lors de la mise à jour des avatars dans les relations:", relationsUpdateError);
          } else {
            // Invalider le cache des relations
            for (const cacheKey of Object.keys(globalCache)) {
              if (cacheKey.includes('relations') || 
                  cacheKey.includes('creator') || 
                  cacheKey.includes('certifier') ||
                  cacheKey.includes('user-relations')) {
                delete globalCache[cacheKey];
              }
            }
          }
        } catch (relationsError) {
          console.error("Exception lors de la mise à jour des avatars dans les relations:", relationsError);
        }
      } else {
        // Si index est fourni, c'est une photo pour le profil de rencontre
        // Récupérer d'abord les photos actuelles
        const { data: profileData } = await supabase
          .from('profiles')
          .select('photos')
          .eq('id', user?.id)
          .single();
        
        const currentPhotos = profileData?.photos || [];
        const updatedPhotos = [...currentPhotos];
        
        // Mettre à jour ou ajouter la nouvelle photo à l'index spécifié
        if (index < updatedPhotos.length) {
          updatedPhotos[index] = urlWithTimestamp;
        } else {
          while (updatedPhotos.length < index) {
            updatedPhotos.push('');
          }
          updatedPhotos.push(urlWithTimestamp);
        }
        
        // Mettre à jour le profil avec le nouveau tableau de photos
        const { error: updateError } = await updateProfile({
          photos: updatedPhotos,
        }, false); // Désactiver l'invalidation complète du cache

        if (updateError) throw updateError;
      }

      // Utiliser l'invalidation ciblée du cache photos au lieu de l'invalidation complète
      await safeInvalidatePhotoCache();

      return { publicUrl: urlWithTimestamp, error: null };
    } catch (error) {
      console.error('Erreur lors du téléchargement de la photo:', error);
      return { publicUrl: null, error };
    }
  };

  const deleteProfilePhoto = async (index: number | null = null) => {
    try {
      if (index === null) {
        // Supprimer la photo de profil principale
        // Obtenir d'abord l'URL actuelle pour extraire le chemin du fichier
        const { data: profileData } = await supabase
          .from('profiles')
          .select('avatar_url')
          .eq('id', user?.id)
          .single();
        
        const avatarUrl = profileData?.avatar_url;
        
        if (avatarUrl) {
          // Extraire le chemin du fichier à partir de l'URL
          const urlObj = new URL(avatarUrl);
          const path = urlObj.pathname.split('/').slice(-2).join('/');
          
          // Supprimer le fichier du stockage
          await supabase.storage
            .from('profilephotos')
            .remove([path]);
        }
        
        // Mettre à jour le profil pour enlever l'URL de l'avatar
        await updateProfile({
          avatar_url: null,
        }, false); // Désactiver l'invalidation complète du cache
      } else {
        // Supprimer une photo du profil de rencontre
        // Récupérer d'abord toutes les photos
        const { data: profileData } = await supabase
          .from('profiles')
          .select('photos')
          .eq('id', user?.id)
          .single();
        
        const currentPhotos = profileData?.photos || [];
        
        // Vérifier si l'index existe
        if (index < currentPhotos.length && currentPhotos[index]) {
          // Extraire le chemin du fichier à partir de l'URL
          const photoUrl = currentPhotos[index];
          const urlObj = new URL(photoUrl);
          const path = urlObj.pathname.split('/').slice(-2).join('/');
          
          // Supprimer le fichier du stockage
          await supabase.storage
            .from('profilephotos')
            .remove([path]);
          
          // Mettre à jour le tableau de photos
          const updatedPhotos = [...currentPhotos];
          updatedPhotos[index] = '';
          
          // Mettre à jour le profil
          await updateProfile({
            photos: updatedPhotos,
          }, false); // Désactiver l'invalidation complète du cache
        }
      }
      
      // Utiliser l'invalidation ciblée du cache photos au lieu de l'invalidation complète
      await safeInvalidatePhotoCache();
      
      return { error: null };
    } catch (error) {
      console.error('Erreur lors de la suppression de la photo:', error);
      return { error };
    }
  };

  const resetContactClicks = async () => {
    try {
      if (!user?.id) return { error: new Error('Utilisateur non connecté') };
      
      const { error } = await supabase.rpc('reset_contact_clicks', {
        profile_id: user.id
      });
      
      if (error) {
        console.error('Erreur lors de la réinitialisation du compteur de clics:', error);
        return { error };
      }
      
      // Invalider le cache après la réinitialisation
      await invalidateCache();
      
      return { error: null };
    } catch (error) {
      console.error('Exception lors de la réinitialisation du compteur de clics:', error);
      return { error };
    }
  };

  return {
    profile,
    updateProfile,
    uploadAvatar,
    uploadProfilePhoto,
    deleteProfilePhoto,
    resetContactClicks,
    refresh
  };
} 